// Bulk edit for payment acceptances (תקבולים / תשלומים).
// Cards live on the payment lines, so instead of document-level debit/credit + VAT + sort code
// (BulkEditModal), each line type gets its own debit / credit card. After saving, a changed
// method card (receipt debit / payment credit) can be offered as the case default and,
// optionally, replaced on other payments that still use the old default.
import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import {
    Portal, IconButton, Tooltip, Box, TextField, FormControlLabel, Checkbox, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Cancel';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Draggable from 'react-draggable';
import CustomMonthDatePicker from '../../Widgets/CustomDates/CustomMonthDatePicker/CustomMonthDatePicker';
import AutocompleteSelectFilter from '../../Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter.jsx';
import DocumentService from '../../../Services/documents.service';
import EnumsService from '../../../Services/enums.service';
import useCaseSystemAccountSettings from '../../../hooks/useCaseSystemAccountSettings';
import {
    getPaymentAcceptanceMethodAccountId,
    getPaymentMethodSettingField,
    getPaymentMethodSide,
} from '../../../Utils/paymentAcceptanceLineAccounts';
import fieldStyles from '../../JournalEntryies/SubComponents/AddJournalEntryModal.module.scss';
import modalStyles from '../../JournalEntryies/SubComponents/JournalEntryTemplatesModal.module.scss';
import shellStyles from '../../PrimaryBulkActionsToolbar/BulkEditModal.module.scss';
import styles from './PaymentAcceptanceBulkEditModal.module.scss';

const RECEIPT = 'תקבול';
const PAYMENT = 'תשלום';

const LINE_TYPES = [
    { key: 'checks', labelKey: 'Checks' },
    { key: 'creditCards', labelKey: 'CreditCards' },
    { key: 'cash', labelKey: 'Cash' },
    { key: 'bankTransactions', labelKey: 'BankTransactions' },
    { key: 'offsets', labelKey: 'Offsets' },
];

const EMPTY_HEADER = {
    reportingMonthDate: null,
    registrationMonthDate: null,
    accountCodeId: '',
    withholdingTax: '',
    description: '',
    reference: '',
};

const emptyLineCards = () =>
    Object.fromEntries(LINE_TYPES.map(({ key }) => [key, { debitAccount: '', creditAccount: '' }]));

// SystemAccountSettingsDialog label keys follow "SystemAccount" + field without the "CardId" suffix.
const settingLabelKey = (field) => `SystemAccount${field.replace(/CardId$/, '')}`;

export default function PaymentAcceptanceBulkEditModal({
    open,
    ids = [],
    selectedItems = [],
    onClose,
    onSavePatch,        // async (patch) => boolean (true when the bulk update succeeded)
    onDefaultsApplied,  // async () => void — refresh after replacing cards on other payments
    formattedAccountCardOptions = [],
    client,
    token = '',
    canEditDefaults = true,
}) {
    const { t, i18n } = useTranslation();
    const dir = i18n.dir();
    const [container] = useState(() => document.createElement('div'));
    const nodeRef = useRef(null);

    // The parent clears its selection after saving; keep what was selected when the modal opened.
    const [selection] = useState(() => ({ ids: [...ids], items: [...selectedItems] }));

    const [busy, setBusy] = useState(false);
    const [header, setHeader] = useState(EMPTY_HEADER);
    const [lineCards, setLineCards] = useState(emptyLineCards);
    const [swapLines, setSwapLines] = useState(false);

    const [suggestions, setSuggestions] = useState(null);

    const { settings: caseSystemSettings } = useCaseSystemAccountSettings(client?.caseId, token);

    const partyOptions = useMemo(
        () =>
            (formattedAccountCardOptions || []).map((o) => ({
                ...o,
                value: o.value ?? o.cardId,
                label: o.label || `${o.cardId} - ${o.name}`,
            })),
        [formattedAccountCardOptions]
    );

    const lineCardOptions = useMemo(
        () =>
            (formattedAccountCardOptions || []).map((o) => ({
                ...o,
                value: o.value ?? o.cardId,
                label: `${o.cardId} - ${o.name}`,
            })),
        [formattedAccountCardOptions]
    );

    const findOption = useCallback(
        (options, id) =>
            id == null || id === ''
                ? null
                : options.find((o) => String(o.value) === String(id)) || { value: id, label: String(id) },
        []
    );

    const cardName = useCallback(
        (id) => {
            const opt = lineCardOptions.find((o) => String(o.value) === String(id));
            return opt ? opt.label : String(id);
        },
        [lineCardOptions]
    );

    const selectionTypes = useMemo(() => {
        const types = new Set(selection.items.map((i) => i.paymentAcceptanceType));
        return { hasReceipts: types.has(RECEIPT), hasPayments: types.has(PAYMENT) };
    }, [selection.items]);

    const lineTypeCounts = useMemo(
        () =>
            Object.fromEntries(
                LINE_TYPES.map(({ key }) => [
                    key,
                    selection.items.filter((i) => Array.isArray(i[key]) && i[key].length > 0).length,
                ])
            ),
        [selection.items]
    );

    const comboInputProps = useMemo(() => ({ disableUnderline: true }), []);
    const inputStyle = useMemo(() => ({ style: { textAlign: dir === 'ltr' ? 'left' : 'right' } }), [dir]);

    const setHeaderField = (k, v) => setHeader((prev) => ({ ...prev, [k]: v }));

    const setLineCard = (lineType, side, value) =>
        setLineCards((prev) => ({ ...prev, [lineType]: { ...prev[lineType], [side]: value } }));

    const swapLineCardInputs = (lineType) =>
        setLineCards((prev) => ({
            ...prev,
            [lineType]: { debitAccount: prev[lineType].creditAccount, creditAccount: prev[lineType].debitAccount },
        }));

    // A changed method card (receipt debit / payment credit) may become the case default.
    const buildSuggestions = useCallback(() => {
        const result = [];
        LINE_TYPES.forEach(({ key, labelKey }) => {
            [
                { paymentType: RECEIPT, present: selectionTypes.hasReceipts },
                { paymentType: PAYMENT, present: selectionTypes.hasPayments },
            ].forEach(({ paymentType, present }) => {
                if (!present) return;
                const side = getPaymentMethodSide(paymentType);
                const newCard = String(lineCards[key][`${side}Account`] || '').trim();
                if (!newCard) return;
                const oldCard = String(
                    getPaymentAcceptanceMethodAccountId(key, caseSystemSettings, paymentType) || ''
                );
                if (newCard === oldCard) return;
                result.push({
                    id: `${key}-${paymentType}`,
                    lineType: key,
                    lineLabelKey: labelKey,
                    paymentType,
                    side,
                    field: getPaymentMethodSettingField(key, paymentType),
                    oldCard,
                    newCard,
                    setDefault: true,
                    replaceExisting: false,
                    matches: null,
                });
            });
        });
        return result;
    }, [lineCards, selectionTypes, caseSystemSettings]);

    // Count other payments still on the old card so the user can decide whether to replace them.
    useEffect(() => {
        if (!suggestions || suggestions.every((s) => s.matches !== null) || !client?.caseId) return;
        let cancelled = false;
        (async () => {
            const counted = await Promise.all(
                suggestions.map(async (s) => {
                    if (!s.oldCard) return { ...s, matches: { documents: 0, lines: 0 } };
                    try {
                        const res = await DocumentService.ReplacePaymentLineCard(
                            {
                                caseId: client.caseId,
                                lineType: s.lineType,
                                paymentAcceptanceType: s.paymentType,
                                side: s.side,
                                oldCardId: s.oldCard,
                                newCardId: s.newCard,
                                dryRun: true,
                            },
                            token
                        );
                        return { ...s, matches: res.ok ? await res.json() : { documents: 0, lines: 0 } };
                    } catch {
                        return { ...s, matches: { documents: 0, lines: 0 } };
                    }
                })
            );
            if (!cancelled) setSuggestions(counted);
        })();
        return () => { cancelled = true; };
    }, [suggestions, client?.caseId, token]);

    useEffect(() => {
        if (!open) return undefined;
        document.body.appendChild(container);
        return () => { try { document.body.removeChild(container); } catch { /* ignore */ } };
    }, [open, container]);

    if (!open) return null;

    const handleSave = async () => {
        const patch = {};
        if (header.reportingMonthDate) patch.reportingMonthDate = header.reportingMonthDate;
        if (header.registrationMonthDate) patch.registrationMonthDate = header.registrationMonthDate;
        if (header.accountCodeId) {
            patch.accountCodeId = String(header.accountCodeId);
            const party = partyOptions.find((o) => String(o.value) === String(header.accountCodeId));
            if (party?.name) patch.accountCodeName = party.name;
        }
        if (String(header.withholdingTax).trim() !== '') patch.withholdingTax = Number(header.withholdingTax);
        if (header.description.trim()) patch.description = header.description.trim();
        if (header.reference.trim()) patch.reference = header.reference.trim();

        const paymentLineCards = Object.fromEntries(
            Object.entries(lineCards)
                .map(([key, cards]) => [key, {
                    debitAccount: String(cards.debitAccount || '').trim() || null,
                    creditAccount: String(cards.creditAccount || '').trim() || null,
                }])
                .filter(([, cards]) => cards.debitAccount || cards.creditAccount)
        );

        if (!Object.keys(patch).length && !Object.keys(paymentLineCards).length && !swapLines) {
            onClose();
            return;
        }

        setBusy(true);
        const ok = await onSavePatch({ ...patch, flipCards: swapLines, paymentLineCards });
        setBusy(false);
        if (!ok) return;

        const next = canEditDefaults ? buildSuggestions() : [];
        if (next.length === 0) {
            onClose();
            return;
        }
        setSuggestions(next);
    };

    const updateSuggestion = (id, changes) =>
        setSuggestions((prev) => prev.map((s) => (s.id === id ? { ...s, ...changes } : s)));

    const handleApplySuggestions = async () => {
        const toDefault = suggestions.filter((s) => s.setDefault && s.field);
        const toReplace = suggestions.filter((s) => s.replaceExisting && s.oldCard && s.matches?.lines > 0);
        if (!toDefault.length && !toReplace.length) {
            onClose();
            return;
        }

        setBusy(true);
        try {
            if (toDefault.length) {
                const payload = Object.fromEntries(toDefault.map((s) => [s.field, s.newCard]));
                const res = await EnumsService.UpdateCaseSystemAccountSettings(client.caseId, payload, token);
                if (!res.ok) throw new Error(await res.text());
            }

            let replacedLines = 0;
            for (const s of toReplace) {
                const res = await DocumentService.ReplacePaymentLineCard(
                    {
                        caseId: client.caseId,
                        lineType: s.lineType,
                        paymentAcceptanceType: s.paymentType,
                        side: s.side,
                        oldCardId: s.oldCard,
                        newCardId: s.newCard,
                    },
                    token
                );
                if (!res.ok) throw new Error(await res.text());
                replacedLines += (await res.json())?.lines || 0;
            }

            if (toDefault.length) toast.success(t('SystemAccountSettingsSaved'));
            if (toReplace.length) {
                toast.success(t('PaymentLineCardsReplaced', { count: replacedLines }));
                if (onDefaultsApplied) await onDefaultsApplied();
            }
            onClose();
        } catch (err) {
            toast.error(err?.message || t('documentUpdateError'));
        } finally {
            setBusy(false);
        }
    };

    const paymentTypeLabel = (paymentType) =>
        paymentType === RECEIPT ? t('PaymentLineReceipts') : t('PaymentLinePayments');

    const renderSuggestions = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
            <div className={styles.hint}>{t('PaymentLineDefaultSuggestionInfo')}</div>
            {suggestions.map((s) => (
                <div key={s.id} className={styles.suggestion}>
                    <div className={styles.suggestionTitle}>
                        {t(s.lineLabelKey)} · {paymentTypeLabel(s.paymentType)} ·{' '}
                        {s.side === 'debit' ? t('Debit Account') : t('Credit Account')}
                    </div>
                    <div className={styles.cardChange}>
                        {s.oldCard ? cardName(s.oldCard) : '—'} {dir === 'rtl' ? '←' : '→'} {cardName(s.newCard)}
                    </div>
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={s.setDefault}
                                disabled={busy || !s.field}
                                onChange={(e) => updateSuggestion(s.id, { setDefault: e.target.checked })}
                            />
                        }
                        label={t('PaymentLineSetAsDefault', { field: t(settingLabelKey(s.field || '')) })}
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={s.replaceExisting}
                                disabled={busy || !s.oldCard || !s.matches || s.matches.lines === 0}
                                onChange={(e) => updateSuggestion(s.id, { replaceExisting: e.target.checked })}
                            />
                        }
                        label={
                            s.matches === null ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <CircularProgress size={12} /> {t('Loading')}
                                </span>
                            ) : (
                                t('PaymentLineReplaceExisting', {
                                    count: s.matches.documents,
                                    oldCard: s.oldCard,
                                    newCard: s.newCard,
                                })
                            )
                        }
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                    />
                </div>
            ))}
        </div>
    );

    const renderForm = () => (
        <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1, p: 1, borderRadius: 1, border: '1px solid #e5e7f6', bgcolor: 'grey.50' }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={swapLines}
                            onChange={(e) => setSwapLines(e.target.checked)}
                            disabled={busy}
                            size="small"
                            sx={{ color: '#304FFF', '&.Mui-checked': { color: '#304FFF' } }}
                        />
                    }
                    label={t('SwapPaymentLineCardsForSelected')}
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.825rem', lineHeight: 1.2 } }}
                />
            </Box>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                <div className={shellStyles.formGrid2}>
                    <div>
                        <div className={modalStyles.fieldLabel}>{t('MonthReportDate')}</div>
                        <div className={shellStyles.dateField}>
                            <CustomMonthDatePicker
                                date={header.reportingMonthDate}
                                onDateChange={(d) => d && setHeaderField('reportingMonthDate', d.toISOString())}
                                height="40px"
                            />
                        </div>
                    </div>
                    <div>
                        <div className={modalStyles.fieldLabel}>{t('MonthRegisterDate')}</div>
                        <div className={shellStyles.dateField}>
                            <CustomMonthDatePicker
                                date={header.registrationMonthDate}
                                onDateChange={(d) => d && setHeaderField('registrationMonthDate', d.toISOString())}
                                height="40px"
                            />
                        </div>
                    </div>
                </div>

                <div className={shellStyles.formGrid2}>
                    <div>
                        <div className={modalStyles.fieldLabel}>{t('SupplierCustomerName')}</div>
                        <AutocompleteSelectFilter
                            options={partyOptions}
                            selectedValues={findOption(partyOptions, header.accountCodeId)}
                            onChange={(_, nv) => setHeaderField('accountCodeId', nv ? nv.value : '')}
                            placeholder={t('NoChange')}
                            isMultiple={false}
                            direction={dir}
                            searchable
                            clearable
                            portalMenu
                        />
                    </div>
                    <div>
                        <div className={modalStyles.fieldLabel}>{t('WithholdingTax')}</div>
                        <TextField
                            fullWidth
                            type="number"
                            placeholder={t('NoChange')}
                            value={header.withholdingTax}
                            onChange={(e) => setHeaderField('withholdingTax', e.target.value)}
                            variant="standard"
                            InputProps={comboInputProps}
                            className={fieldStyles.styleInput}
                            size="small"
                            inputProps={inputStyle}
                        />
                    </div>
                </div>

                <div className={shellStyles.formGrid2}>
                    <div>
                        <div className={modalStyles.fieldLabel}>{t('enterDescription')}</div>
                        <TextField
                            fullWidth
                            placeholder={t('NoChange')}
                            value={header.description}
                            onChange={(e) => setHeaderField('description', e.target.value)}
                            variant="standard"
                            InputProps={comboInputProps}
                            className={fieldStyles.styleInput}
                            size="small"
                            inputProps={inputStyle}
                        />
                    </div>
                    <div>
                        <div className={modalStyles.fieldLabel}>{t('Reference')}</div>
                        <TextField
                            fullWidth
                            placeholder={t('NoChange')}
                            value={header.reference}
                            onChange={(e) => setHeaderField('reference', e.target.value)}
                            variant="standard"
                            InputProps={comboInputProps}
                            className={fieldStyles.styleInput}
                            size="small"
                            inputProps={inputStyle}
                        />
                    </div>
                </div>

                <div className={styles.sectionTitle}>{t('ReceiptPaymentItems')}</div>
                <div className={styles.hint}>{t('PaymentLineCardsHint')}</div>

                <div className={styles.lineHeader}>
                    <div />
                    <div>{t('Debit Account')}</div>
                    <div />
                    <div>{t('Credit Account')}</div>
                </div>

                {LINE_TYPES.map(({ key, labelKey }) => (
                    <div key={key} className={`${styles.lineRow}${lineTypeCounts[key] === 0 ? ` ${styles.lineMuted}` : ''}`}>
                        <div className={styles.lineLabel}>
                            {t(labelKey)}
                            <Tooltip title={t('PaymentLineDocumentsWithType', { count: lineTypeCounts[key] })}>
                                <span className={styles.lineCount}>{lineTypeCounts[key]}</span>
                            </Tooltip>
                        </div>
                        <AutocompleteSelectFilter
                            options={lineCardOptions}
                            selectedValues={findOption(lineCardOptions, lineCards[key].debitAccount)}
                            onChange={(_, nv) => setLineCard(key, 'debitAccount', nv ? nv.value : '')}
                            placeholder={t('NoChange')}
                            isMultiple={false}
                            direction={dir}
                            searchable
                            clearable
                            portalMenu
                        />
                        <Tooltip title={t('SwapAccounts')}>
                            <IconButton size="small" sx={{ p: 0.5 }} onClick={() => swapLineCardInputs(key)} disabled={busy}>
                                <SwapHorizIcon sx={{ fontSize: 18, color: '#304FFF' }} />
                            </IconButton>
                        </Tooltip>
                        <AutocompleteSelectFilter
                            options={lineCardOptions}
                            selectedValues={findOption(lineCardOptions, lineCards[key].creditAccount)}
                            onChange={(_, nv) => setLineCard(key, 'creditAccount', nv ? nv.value : '')}
                            placeholder={t('NoChange')}
                            isMultiple={false}
                            direction={dir}
                            searchable
                            clearable
                            portalMenu
                        />
                    </div>
                ))}
            </div>
        </>
    );

    const inSuggestionStep = suggestions !== null;

    return (
        <Portal container={container}>
            <div
                className={`${shellStyles.overlay} ${dir === 'rtl' ? shellStyles.rtl : shellStyles.ltr}`}
                onClick={busy ? undefined : onClose}
            >
                <Draggable
                    handle=".pa-bulk-edit-drag-handle"
                    cancel="button, .MuiIconButton-root, input, textarea, [class*='trigger']"
                    nodeRef={nodeRef}
                >
                    <div ref={nodeRef} className={shellStyles.dragShell} dir={dir} onClick={(e) => e.stopPropagation()}>
                        <div className={shellStyles.modal}>
                            <div className={modalStyles.body}>
                                <div className={`${modalStyles.header} pa-bulk-edit-drag-handle ${shellStyles.dragHandle}`}>
                                    <Tooltip title={t('Cancel')}>
                                        <IconButton onClick={onClose} className={modalStyles.closeIcon} disabled={busy}>
                                            <CloseIcon className={modalStyles.closeIcon} />
                                        </IconButton>
                                    </Tooltip>

                                    <div>
                                        {inSuggestionStep
                                            ? t('PaymentLineDefaultSuggestionTitle')
                                            : `${t('BulkEdit')} (${selection.ids.length}) ${t('Selected')}`}
                                    </div>

                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        {inSuggestionStep && (
                                            <button type="button" className={modalStyles.ghostBtn} onClick={onClose} disabled={busy}>
                                                {t('PaymentLineSkip')}
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className={modalStyles.primaryBtn}
                                            onClick={inSuggestionStep ? handleApplySuggestions : handleSave}
                                            disabled={busy}
                                        >
                                            {inSuggestionStep ? t('Apply') : t('Save')}
                                        </button>
                                    </div>
                                </div>

                                <div className={modalStyles.scroll}>
                                    {inSuggestionStep ? renderSuggestions() : renderForm()}
                                </div>
                            </div>
                        </div>
                    </div>
                </Draggable>
            </div>
        </Portal>
    );
}
